import React, { useState, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
} from "chart.js";
import { Icons } from "./Machine360Icons";
import Nodata from "../common-component/Nodata";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip
);

const PERIOD_OPTIONS = [
  { value: 7, label: "Last 7 Days" },
  { value: 30, label: "Last 30 Days" },
  { value: 90, label: "Last 90 Days" },
];

export const ResourceUsageSection = ({
  data,
  isLoading = false,
  selectedDays = 30,
  onPeriodChange,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(selectedDays);

  useEffect(() => {
    setSelectedPeriod(selectedDays);
  }, [selectedDays]);

  const handlePeriodChange = (e) => {
    const val = Number(e.target.value);
    setSelectedPeriod(val);
    if (onPeriodChange) {
      onPeriodChange(val);
    }
  };

  if (isLoading) {
    return (
      <div className="m360-card m360-resource-usage-card">
        <div className="m360-card-header">
          <div className="m360-card-title-group">
            <Skeleton circle={true} height={16} width={16} />
            <Skeleton height={16} width={170} />
          </div>
          <div className="m360-card-actions">
            <Skeleton height={28} width={110} borderRadius={4} />
          </div>
        </div>

        <div className="m360-resource-grid">
          {["CPU", "Memory", "Network"].map((lbl, idx) => (
            <div key={idx} className="m360-resource-col">
              <div className="m360-resource-col-header">
                <Skeleton height={13} width={60} />
                <div style={{ display: "flex", gap: 6 }}>
                  <Skeleton height={18} width={52} borderRadius={4} />
                  <Skeleton height={18} width={52} borderRadius={4} />
                </div>
              </div>
              <div
                className="m360-resource-chart-wrap d-flex align-items-center justify-content-center"
                style={{ height: 110 }}
              >
                <Skeleton height={90} width="100%" borderRadius={4} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const title = data?.title || `Resource Usage (Last ${selectedPeriod} Days)`;
  const hasResourceData = Boolean(
    data?.metrics &&
    Array.isArray(data.metrics) &&
    data.metrics.some(
      (m) =>
        (Array.isArray(m.data) && m.data.some((v) => Number(v) > 0)) ||
        (Array.isArray(m.maxData) && m.maxData.some((v) => Number(v) > 0)) ||
        (m.avg && m.avg !== "0%" && m.avg !== "0 MB" && m.avg !== "0" && m.avg !== 0) ||
        (m.max && m.max !== "0%" && m.max !== "0 MB" && m.max !== "0" && m.max !== 0)
    )
  );

  return (
    <div className="m360-card m360-resource-usage-card">
      <div className="m360-card-header">
        <div className="m360-card-title-group">
          <Icons.Gear size={16} className="m360-card-header-icon" />
          <h4 className="m360-card-title">{title}</h4>
        </div>
        <div className="m360-card-actions">
          <select
            className="m360-select-dropdown"
            value={selectedPeriod}
            onChange={handlePeriodChange}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!hasResourceData ? (
        <div className="m360-card-nodata-wrapper">
          <Nodata />
        </div>
      ) : (
        <div className="m360-resource-grid">
          {data?.metrics?.map((metric) => {
            const isMB = metric.unit === "MB" || metric.id === "network";

            const datasets = [
              {
                data: metric.data,
                borderColor: metric.lineColor,
                backgroundColor: metric.fillColor,
                fill: true,
                tension: 0.35,
                pointBackgroundColor: metric.lineColor,
                pointBorderColor: "#ffffff",
                pointBorderWidth: 1.5,
                pointRadius: 2.5,
                borderWidth: 1.8,
              },
            ];

            if (Array.isArray(metric.maxData) && metric.maxData.length > 0) {
              datasets.push({
                label: "Max",
                data: metric.maxData,
                borderColor: metric.maxLineColor || "#F97316",
                backgroundColor: "transparent",
                fill: false,
                tension: 0.35,
                pointBackgroundColor: metric.maxLineColor || "#F97316",
                pointBorderColor: "#ffffff",
                pointBorderWidth: 1.5,
                pointRadius: 2.5,
                borderWidth: 1.8,
              });
            }

            const miniChartData = {
              labels: metric.labels,
              datasets,
            };

            const miniChartOptions = {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  backgroundColor: "rgba(17, 17, 17, 0.92)",
                  padding: 6,
                  cornerRadius: 4,
                  titleFont: { family: "'Sora', sans-serif", size: 10 },
                  bodyFont: { family: "'Sora', sans-serif", size: 10 },
                  callbacks: {
                    label: (context) => {
                      const val = Number(context.raw);
                      const formattedVal = isMB
                        ? val.toLocaleString(undefined, {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })
                        : context.raw;
                      return ` ${metric.label}: ${formattedVal}${isMB ? " MB" : "%"}`;
                    },
                  },
                },
              },
              scales: {
                x: {
                  grid: { display: false, drawBorder: false },
                  ticks: {
                    color: "#94a3b8",
                    font: { size: 9 },
                    maxRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 5,
                  },
                },
                y: {
                  min: 0,
                  ...(isMB
                    ? {
                      suggestedMax: 10,
                      ticks: {
                        maxTicksLimit: 3,
                        color: "#94a3b8",
                        font: { size: 9 },
                        callback: (value) =>
                          `${Number(value.toFixed(0)).toLocaleString()} MB`,
                      },
                    }
                    : {
                      max: 100,
                      ticks: {
                        stepSize: 50,
                        color: "#94a3b8",
                        font: { size: 9 },
                        callback: (value) => `${value}%`,
                      },
                    }),
                  grid: {
                    color: "#f8fafc",
                    drawBorder: false,
                  },
                },
              },
            };

            return (
              <div key={metric.id} className="m360-resource-col">
                <div className="m360-resource-col-header">
                  <span className="m360-resource-col-title">{metric.label}</span>
                  <div className="m360-resource-badges">
                    {metric.extraLabel && (
                      <span
                        className="m360-resource-set-badge"
                        style={{
                          backgroundColor:
                            metric.extraBadgeBg || "#f4f6f8",
                          color:
                            metric.extraBadgeColor || "var(--grey4a, #4a4a4a)",
                        }}
                      >
                        {metric.extraLabel}
                      </span>
                    )}
                    <span
                      className="m360-resource-avg-badge"
                      style={{
                        backgroundColor: metric.badgeBg,
                        color: metric.badgeColor,
                      }}
                    >
                      Avg: {metric.avg}
                    </span>
                    {metric.max && (
                      <span
                        className="m360-resource-max-badge"
                        style={{
                          backgroundColor:
                            metric.maxBadgeBg || "rgba(249, 115, 22, 0.12)",
                          color: metric.maxBadgeColor || "#F97316",
                        }}
                      >
                        Max: {metric.max}
                      </span>
                    )}
                  </div>
                </div>
                <div className="m360-resource-chart-wrap">
                  <Line data={miniChartData} options={miniChartOptions} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

